import {
  ORDER_DELIVERY_METHOD,
  OrderDeliveryMethod,
  STATUS_LABEL,
  StatusLabel,
} from "../../common/constants/order-display.constants";
import { statusLabelText } from "../../common/helpers/order-display.util";
import type { ProgressStep, ProgressStepState } from "./order-tracking.types";

const SHARED_STEPS: StatusLabel[] = [
  STATUS_LABEL.placing,
  STATUS_LABEL.created,
  STATUS_LABEL.processing,
];

const STEPS_BY_DELIVERY_METHOD: Record<OrderDeliveryMethod, StatusLabel[]> = {
  [ORDER_DELIVERY_METHOD.ship]: [...SHARED_STEPS, STATUS_LABEL.shipped, STATUS_LABEL.delivered],
  [ORDER_DELIVERY_METHOD.pickup]: [
    ...SHARED_STEPS,
    STATUS_LABEL.readyForPickup,
    STATUS_LABEL.pickedUp,
  ],
};

/** An outcome replaces the step at `stepIndex` and ends the steps. */
const OUTCOMES: Partial<Record<StatusLabel, { stepIndex: number; state: "failed" | "cancelled" }>> =
  {
    [STATUS_LABEL.creationFailed]: { stepIndex: 1, state: "failed" },
    [STATUS_LABEL.cancelled]: { stepIndex: 2, state: "cancelled" },
    [STATUS_LABEL.rejected]: { stepIndex: 2, state: "failed" },
    [STATUS_LABEL.problem]: { stepIndex: 2, state: "failed" },
  };

const stateFor = (index: number, currentIndex: number): ProgressStepState => {
  if (index < currentIndex) {
    return "complete";
  }
  return index === currentIndex ? "current" : "upcoming";
};

/** The last step being the status means every step is complete. Undefined when there is no status or it is not one of the steps. */
export const progressFor = (
  status: StatusLabel | undefined,
  deliveryMethod: OrderDeliveryMethod
): ProgressStep[] | undefined => {
  if (!status) {
    return undefined;
  }
  const steps = STEPS_BY_DELIVERY_METHOD[deliveryMethod];
  const outcome = OUTCOMES[status];
  if (outcome) {
    return [
      ...steps
        .slice(0, outcome.stepIndex)
        .map((step): ProgressStep => ({ label: statusLabelText(step), state: "complete" })),
      { label: statusLabelText(status), state: outcome.state },
    ];
  }

  const statusIndex = steps.indexOf(status);
  if (statusIndex === -1) {
    return undefined;
  }
  const currentIndex = statusIndex === steps.length - 1 ? steps.length : statusIndex;
  return steps.map((step, index) => ({
    label: statusLabelText(step),
    state: stateFor(index, currentIndex),
  }));
};
